/**
 * Demo state — the mutable half of the sample-data layer.
 *
 * Sample datasets are frozen fixtures; this holds what the *user* does to them
 * during a run: a record they created, a status they approved, a row they
 * deleted. Resolvers read it when building a response so the screen reflects
 * the tour's own actions, and it is wiped at the start of every run so two
 * tours never see each other's leftovers.
 *
 * Nothing here is persisted — a page refresh ends the run, and the tour
 * restarts from a clean demo state.
 */

import { releaseTourDocumentUrls } from '../data/tour-sample-documents';

/** A simulated mutation, recorded instead of being sent to the backend. */
export interface TourMutationRecord {
  method: string;
  url: string;
  body: unknown;
  at: number;
}

interface TourDemoState {
  /** Records created during the run, keyed by collection (e.g. 'users'). */
  created: Map<string, Record<string, unknown>[]>;
  /** Field-level edits applied to existing sample rows: collection → id → patch. */
  patched: Map<string, Map<string, Record<string, unknown>>>;
  /** Ids removed during the run, keyed by collection. */
  removed: Map<string, Set<string>>;
  /** Everything the user "saved" this run, for debugging a tour. */
  mutations: TourMutationRecord[];
  /** Interactive selections the tour steps key off. */
  selections: Record<string, unknown>;
}

const emptyState = (): TourDemoState => ({
  created: new Map(),
  patched: new Map(),
  removed: new Map(),
  mutations: [],
  selections: {},
});

let state: TourDemoState = emptyState();

/** Wipe the demo state. Called whenever a tour starts or stops. */
export const resetTourDemoState = () => {
  state = emptyState();
  // Demo documents are handed out as blob URLs; drop them with the rest of the
  // run's state so they are not left allocated.
  releaseTourDocumentUrls();
};

/** Record a simulated create so the collection's next read includes it. */
export const addTourRecord = (
  collection: string,
  record: Record<string, unknown>
) => {
  const list = state.created.get(collection) ?? [];
  list.unshift(record);
  state.created.set(collection, list);
};

/** Record a simulated edit/approval as a patch over an existing sample row. */
export const patchTourRecord = (
  collection: string,
  id: string | number,
  patch: Record<string, unknown>
) => {
  const byId = state.patched.get(collection) ?? new Map();
  const existing = byId.get(String(id)) ?? {};
  byId.set(String(id), { ...existing, ...patch });
  state.patched.set(collection, byId);
};

/** Record a simulated delete. The row disappears without a DELETE request. */
export const removeTourRecord = (collection: string, id: string | number) => {
  const ids = state.removed.get(collection) ?? new Set<string>();
  ids.add(String(id));
  state.removed.set(collection, ids);
};

/** Note a mutation the tour swallowed, for troubleshooting a flow. */
export const recordTourMutation = (record: TourMutationRecord) => {
  state.mutations.push(record);
};

/** Every mutation simulated so far this run. */
export const getTourMutations = (): TourMutationRecord[] => [
  ...state.mutations,
];

/** Remember an interactive choice (selected employee, active tab, …). */
export const setTourSelection = (key: string, value: unknown) => {
  state.selections[key] = value;
};

/** Read back an interactive choice. */
export const getTourSelection = <T>(key: string): T | undefined =>
  state.selections[key] as T | undefined;

/**
 * A sample collection as the tour currently sees it: the fixture rows with
 * this run's creates, edits and deletes folded in.
 *
 * `idKey` names the field the collection is keyed by, so a patch recorded
 * against `AWD-2024-0142` lands on the right row.
 */
export const applyTourDemoState = <T extends object>(
  collection: string,
  rows: readonly T[],
  idKey: keyof T
): T[] => {
  const created = (state.created.get(collection) ?? []) as T[];
  const patched = state.patched.get(collection);
  const removed = state.removed.get(collection);

  return [...created, ...rows]
    .filter(row => !removed?.has(String(row[idKey])))
    .map(row => {
      const patch = patched?.get(String(row[idKey]));
      return patch ? ({ ...row, ...patch } as T) : row;
    });
};
